<!-- eslint-disable vue/no-setup-props-destructure -->
<script setup lang="ts">
import { Check, Close, Edit, FullScreen } from '@element-plus/icons-vue'
import type { CheckboxValueType } from 'element-plus'
import { DateTime } from 'luxon'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import CompletedIcon from '~/components/icons/CompletedIcon.vue'
import InProgressIcon from '~/components/icons/InProgressIcon.vue'
import RouteMap from '~/components/RouteMap.vue'
import { useVisitsStore } from '~/stores/visits'
import { calculateDuration } from '~/utils/dateTimeUtils'

// Props and emits
const props = defineProps<{
  modelValue: 'streets' | 'satellite'
}>()

const emit = defineEmits<{
  (e: 'update:modelValue', value: 'streets' | 'satellite'): void
}>()

// State
const isFullScreen = ref(false)
const regularMapLoading = ref(false) // Separate loading for regular map
const fullScreenMapLoading = ref(false) // Separate loading for fullscreen map

const mapStyle = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
})

const visitsStore = useVisitsStore()
const { selectedVisit, visitDetails, status } = storeToRefs(visitsStore)

// Function declarations
const handleNotBillableChange = (value: CheckboxValueType) => {
  visitsStore.setNotBillable(value === true)
}

const toggleFullScreen = () => {
  isFullScreen.value = !isFullScreen.value
}

// Generate Samsara URL for device tracking
const generateSamsaraUrl = (
  deviceId: string,
  arrivalTimestamp: string,
  departureTimestamp?: string
) => {
  if (!arrivalTimestamp) {
    return `https://cloud.samsara.com/devices/${deviceId}/vehicle`
  }

  const arrivalMs = new Date(arrivalTimestamp).getTime()

  // Validate arrival timestamp
  if (isNaN(arrivalMs)) {
    return `https://cloud.samsara.com/devices/${deviceId}/vehicle`
  }

  let endMs: number

  if (!departureTimestamp) {
    // For in-progress visits, use current time
    endMs = Date.now()
  } else {
    endMs = new Date(departureTimestamp).getTime()
    // Validate departure timestamp, fallback to current time if invalid
    if (isNaN(endMs)) {
      endMs = Date.now()
    }
  }

  // Ensure duration is not negative if endMs is before arrivalMs
  const durationMs = Math.max(0, endMs - arrivalMs)
  const durationSeconds = Math.floor(durationMs / 1000)

  return `https://cloud.samsara.com/devices/${deviceId}/vehicle?end_ms=${endMs}&duration=${durationSeconds}`
}

// Helper function to format dates
const formatDateTime = (dateString: string) => {
  return DateTime.fromISO(dateString).toFormat('MMM d, HH:mm')
}

// Helper function to format terminal names
const formatTerminalName = (terminal: string) => {
  return terminal.replace(/_/g, ' ').toUpperCase()
}

// Helper function to get direction color
const getDirectionColor = (direction: string) => {
  return direction === 'inbound' ? 'text-green-600' : 'text-blue-600'
}

// Helper function to get direction icon
const getDirectionIcon = (direction: string) => {
  return direction === 'inbound' ? '↓' : '↑'
}

// Watch for fullscreen changes to handle body scroll
watch(
  () => isFullScreen.value,
  (newValue) => {
    // Prevent body scroll when in fullscreen
    document.body.style.overflow = newValue ? 'hidden' : ''
  }
)
</script>

<template>
  <!-- Full Screen Map Overlay -->
  <Teleport to="body">
    <div v-if="isFullScreen" class="fixed inset-0 z-50 bg-white">
      <div class="h-full flex flex-col">
        <div class="flex items-center justify-between p-4 border-b">
          <div class="flex items-center space-x-4">
            <h2 class="text-xl font-semibold text-gray-900">Route Map</h2>
            <el-radio-group v-model="mapStyle" size="small">
              <el-radio-button value="streets">Street</el-radio-button>
              <el-radio-button value="satellite">Satellite</el-radio-button>
            </el-radio-group>
          </div>
          <el-button type="default" @click="toggleFullScreen">
            <Close class="w-5 h-5" />
          </el-button>
        </div>
        <RouteMap
          v-if="isFullScreen"
          v-model="mapStyle"
          v-model:loading="fullScreenMapLoading"
          :is-full-screen="true"
          :selected-visit="selectedVisit"
          class="flex-1"
        />
      </div>
    </div>
  </Teleport>

  <div v-if="!selectedVisit" class="flex items-center justify-center h-full">
    <div class="text-center">
      <span class="text-4xl mb-4 block">👈</span>
      <p class="text-gray-500">Select a visit from the list to view details</p>
    </div>
  </div>

  <div v-else class="flex flex-col h-full">
    <div class="flex-1 overflow-auto">
      <!-- Conditionally render the regular map container -->
      <div
        v-if="!isFullScreen"
        class="bg-white rounded-lg shadow-sm border border-gray-100"
      >
        <!-- General Information Section -->
        <div class="px-4 py-3">
          <!-- Header with status badge -->
          <div class="flex items-center justify-between mb-3">
            <div class="flex items-baseline space-x-2">
              <span class="text-xl font-bold text-blue-600">
                {{ visitDetails.totalTime }}
              </span>
              <span class="text-lg font-semibold text-gray-900">
                at {{ visitDetails.locationName }} on
                {{
                  selectedVisit
                    ? DateTime.fromISO(selectedVisit.arrival).toFormat('M/d')
                    : ''
                }}
              </span>
            </div>
            <div class="flex items-center">
              <span
                class="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium"
                :class="
                  status === 'completed'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800'
                "
              >
                <component
                  :is="status === 'completed' ? CompletedIcon : InProgressIcon"
                  class="w-3 h-3 mr-1"
                />
                {{ status === 'completed' ? 'Completed' : 'In Progress' }}
              </span>
            </div>
          </div>

          <!-- Compact Info Row -->
          <div class="flex items-center text-xs space-x-2">
            <!-- Visit ID -->
            <span class="text-gray-500 font-medium">ID:</span>
            <span class="font-medium text-gray-900">{{
              selectedVisit?.id ?? '---'
            }}</span>
            <span class="text-gray-400">/</span>
            <!-- Device -->
            <span class="text-gray-500 font-medium">Device:</span>
            <a
              v-if="visitDetails?.externalDeviceId && selectedVisit?.arrival"
              :href="
                generateSamsaraUrl(
                  visitDetails?.externalDeviceId!,
                  selectedVisit?.arrival!,
                  selectedVisit?.departure
                )
              "
              target="_blank"
              rel="noopener noreferrer"
              class="inline-flex items-center font-mono text-blue-600 hover:text-blue-800 hover:underline"
            >
              {{ visitDetails.externalDeviceId }}
              <i-mdi:external-link class="w-3 h-3 ml-1" />
            </a>
            <span v-else class="text-gray-400">No Device</span>
            <span class="text-gray-400">/</span>
            <!-- License -->
            <span class="text-gray-500 font-medium">License:</span>
            <span class="font-medium text-gray-900">
              {{ visitDetails?.licensePlate || 'N/A' }}
            </span>
            <span class="text-gray-400">/</span>
            <!-- Driver -->
            <span class="text-gray-500 font-medium">Driver:</span>
            <span
              v-if="visitDetails?.driverName"
              class="font-medium text-gray-900 truncate"
              :title="visitDetails.driverName"
            >
              {{ visitDetails.driverName }}
            </span>
            <span v-else class="text-gray-400">No Driver</span>
          </div>

          <!-- Appointment -->
          <div
            v-if="visitDetails?.appointment"
            class="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg"
          >
            <div class="flex items-center space-x-2">
              <i-mdi:calendar-clock
                class="w-3 h-3 text-amber-600 flex-shrink-0"
              />
              <span class="text-xs font-medium text-amber-800">{{
                visitDetails.appointment
              }}</span>
            </div>
          </div>
        </div>

        <div class="h-px bg-gray-100" />

        <!-- Appointments and Gate Transactions Side by Side -->
        <div
          v-if="
            selectedVisit?.appointments?.length ||
            selectedVisit?.gate_transactions?.length
          "
          class="px-4 py-2"
        >
          <div class="grid grid-cols-2 gap-4">
            <!-- Appointments Section (Left) -->
            <div>
              <h3
                class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2"
              >
                Appointments ({{ selectedVisit?.appointments?.length || 0 }})
              </h3>
              <div v-if="selectedVisit?.appointments?.length" class="space-y-1">
                <div
                  v-for="(appointment, index) in selectedVisit.appointments"
                  :key="index"
                  class="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-xs"
                >
                  <div class="flex items-center space-x-2 flex-1 min-w-0">
                    <router-link
                      :to="`/containers?selected_container=${appointment.container_number}&status=Searched&page=1&containers=${appointment.container_number}`"
                      class="font-medium text-blue-600 hover:text-blue-800 hover:underline truncate"
                    >
                      {{ appointment.container_number }}
                    </router-link>
                    <span
                      :class="getDirectionColor(appointment.direction)"
                      class="font-medium"
                    >
                      {{ getDirectionIcon(appointment.direction) }}
                    </span>
                    <el-tag
                      :type="appointment.loaded ? 'success' : 'warning'"
                      size="small"
                      effect="plain"
                      class="!text-xs !px-1 !py-0"
                    >
                      {{ appointment.loaded ? 'L' : 'E' }}
                    </el-tag>
                    <span class="text-gray-600 truncate">
                      {{ formatTerminalName(appointment.terminal) }}
                    </span>
                  </div>
                  <div class="text-xs text-gray-500 ml-2 flex-shrink-0">
                    <span v-if="appointment.window_start">
                      {{ formatDateTime(appointment.window_start) }}
                    </span>
                  </div>
                </div>
              </div>
              <div v-else class="text-xs text-gray-400 italic py-1">
                No appointments scheduled
              </div>
            </div>

            <!-- Gate Transactions Section (Right) -->
            <div>
              <h3
                class="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2"
              >
                Gate Transactions ({{
                  selectedVisit?.gate_transactions?.length || 0
                }})
              </h3>
              <div
                v-if="selectedVisit?.gate_transactions?.length"
                class="space-y-1"
              >
                <div
                  v-for="(
                    transaction, index
                  ) in selectedVisit.gate_transactions"
                  :key="index"
                  class="flex items-center justify-between py-1 px-2 bg-gray-50 rounded text-xs"
                >
                  <div class="flex items-center space-x-2">
                    <span
                      :class="getDirectionColor(transaction.direction)"
                      class="font-medium"
                    >
                      {{ getDirectionIcon(transaction.direction) }}
                    </span>
                    <el-tag
                      :type="transaction.loaded ? 'success' : 'warning'"
                      size="small"
                      effect="plain"
                      class="!text-xs !px-1 !py-0"
                    >
                      {{ transaction.loaded ? 'L' : 'E' }}
                    </el-tag>
                    <span class="text-gray-600">
                      {{ formatTerminalName(transaction.terminal) }}
                    </span>
                  </div>
                  <div class="text-xs text-gray-900 font-medium">
                    {{ formatDateTime(transaction.time) }}
                  </div>
                </div>
              </div>
              <div v-else class="text-xs text-gray-400 italic py-1">
                No gate transactions recorded
              </div>
            </div>
          </div>
        </div>

        <div class="h-px bg-gray-100" />

        <!-- Timeline and Financial Section -->
        <div class="px-4 py-3">
          <div class="grid grid-cols-2 gap-6">
            <!-- Timeline Section (Left Column) -->
            <div>
              <h3
                class="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-3"
              >
                Time Breakdown
              </h3>

              <div class="space-y-3">
                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center space-x-2">
                    <div class="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span class="font-medium text-gray-900">Made line</span>
                  </div>
                  <div class="flex items-center space-x-2">
                    <el-button plain size="small" class="!px-2 !py-1" disabled>
                      <Edit class="w-3 h-3" />
                    </el-button>
                    <span class="text-gray-900 font-medium">{{
                      visitDetails?.lineTime
                    }}</span>
                  </div>
                </div>

                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center space-x-2">
                    <div class="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span class="font-medium text-gray-900"
                      >Entered geo-fence</span
                    >
                  </div>
                  <span class="text-gray-900 font-medium">{{
                    visitDetails?.entryTime
                  }}</span>
                </div>

                <div class="flex items-center justify-between text-sm">
                  <div class="flex items-center space-x-2">
                    <div class="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span class="font-medium text-gray-900"
                      >Exit geo-fence</span
                    >
                  </div>
                  <div class="text-gray-900 font-medium">
                    {{ visitDetails?.exitTime }}
                  </div>
                </div>
              </div>

              <!-- Total Duration Summary -->
              <div class="mt-3 pt-3 border-t border-gray-100">
                <div class="flex items-center justify-between text-sm">
                  <div class="text-xs text-gray-500 uppercase tracking-wide">
                    Total Duration
                  </div>
                  <div class="text-sm font-semibold text-gray-900">
                    {{
                      selectedVisit
                        ? calculateDuration(
                            selectedVisit.arrival,
                            selectedVisit.departure
                          )
                        : '---'
                    }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Financial Section (Right Column) -->
            <div class="border-l border-gray-100 pl-6">
              <div class="flex items-start justify-between mb-3">
                <div>
                  <h3
                    class="text-sm font-semibold text-gray-900 uppercase tracking-wide mb-1"
                  >
                    Billing Summary
                  </h3>
                  <div class="text-xs text-gray-600">
                    {{ visitDetails?.totalTime }} at ${{
                      visitDetails?.hourlyRate
                    }}/hour
                  </div>
                </div>
                <div class="text-right">
                  <div class="text-2xl font-bold text-gray-900">
                    ${{ visitDetails?.totalCharge }}
                  </div>
                  <div class="text-xs text-gray-500 uppercase tracking-wide">
                    Total
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <el-checkbox
                  class="!m-0"
                  :model-value="visitDetails?.isNotBillable"
                  size="small"
                  @update:model-value="handleNotBillableChange"
                >
                  <span class="text-xs text-gray-600">
                    Mark as non-billable
                  </span>
                </el-checkbox>
              </div>

              <div class="grid grid-cols-2 gap-2">
                <el-button type="primary" size="small" class="!h-8">
                  <template #icon>
                    <Check class="w-3 h-3" />
                  </template>
                  Approve
                </el-button>
                <el-button type="danger" plain size="small" class="!h-8">
                  <template #icon>
                    <Close class="w-3 h-3" />
                  </template>
                  Reject
                </el-button>
              </div>
            </div>
          </div>
        </div>

        <div class="h-px bg-gray-100" />

        <!-- Map Section -->
        <div class="p-4 border-t border-gray-100">
          <div class="flex items-center justify-between mb-3">
            <h3 class="text-lg font-semibold text-gray-900">Route Map</h3>
            <div class="flex items-center space-x-2">
              <el-radio-group v-model="mapStyle" size="small">
                <el-radio-button value="streets">Street</el-radio-button>
                <el-radio-button value="satellite">Satellite</el-radio-button>
              </el-radio-group>
              <el-button type="default" size="small" @click="toggleFullScreen">
                <FullScreen class="w-4 h-4" />
              </el-button>
            </div>
          </div>
          <RouteMap
            v-model="mapStyle"
            v-model:loading="regularMapLoading"
            :selected-visit="selectedVisit"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.el-button--large {
  height: 44px;
  padding: 0 24px;
}

// Custom compact button styles
.el-button--small {
  height: 28px;
  padding: 0 8px;
  font-size: 12px;
}

// Ensure checkbox is properly sized
.el-checkbox--small .el-checkbox__label {
  font-size: 12px;
}
</style>
